import {useEffect, useState} from 'react';
import {useParams, Link, useHistory} from 'react-router-dom';
import {
  Button,
  Col,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
  Collapse,
  notification,
} from 'antd';

import {Plan, Test} from '../../graphql/API';
import {PlansService} from './PlansService';
import {TestsService} from '../Test';
import {LevelsService} from '../Level/LevelsService';
import {GradesService} from '../Grade/GradesService';
import {CurriculumsService} from '../Curriculums/CurriculumsService';
import {DailyExerciseService} from '@PikaDoApp/npm-pikado-logic';

const plansService = new PlansService();
const testsService = new TestsService();
const levelsService = new LevelsService();
const gradesService = new GradesService();
const curriculumsService = new CurriculumsService();
const dailyExerciseService = new DailyExerciseService();

export const PlanEdit = () => {
  const history = useHistory();

  const [testsSearchResult, setTestsSearchResult] = useState<Array<Test>>();
  const [allFetchedTests, setAllFetchedTests] = useState<Array<Test>>([]);

  const [plan, setPlan] = useState<any | undefined>(undefined);

  // Editable plan metadata state
  const [planName, setPlanName] = useState<string>('');
  const [planDuration, setPlanDuration] = useState<number>(0);
  const [selectedLevelId, setSelectedLevelId] = useState<string | undefined>(
    undefined,
  );
  const [selectedGradeId, setSelectedGradeId] = useState<string | undefined>(
    undefined,
  );
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<
    string | undefined
  >(undefined);
  const [levels, setLevels] = useState<Array<any>>([]);
  const [grades, setGrades] = useState<Array<any>>([]);
  const [curriculums, setCurriculums] = useState<Array<any>>([]);

  const [exerciseMap, setExerciseMap] = useState<{[id: string]: any}>({});
  const [allExercises, setAllExercises] = useState<Array<any>>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Array<any>>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // State for lazy-loaded daily exercises
  const [originalDayExercises, setOriginalDayExercises] = useState<
    Map<number, string[]>
  >(new Map());
  const [currentDayExercises, setCurrentDayExercises] = useState<
    Map<number, string[]>
  >(new Map());
  const [loadedDayDetails, setLoadedDayDetails] = useState<Map<number, any[]>>(
    new Map(),
  );
  const [loadingDays, setLoadingDays] = useState<Set<number>>(new Set());

  let params: {planId: string} = useParams();

  const {Panel} = Collapse;
  const {Title, Text} = Typography;

  // Fetch levels, grades, and curriculums for dropdowns
  useEffect(() => {
    console.log('🔍 [PLAN-EDIT] Fetching levels, grades, and curriculums');

    levelsService
      .getLevels()
      .then(lvls => {
        console.log('✅ [PLAN-EDIT] Levels fetched:', lvls);
        setLevels(lvls);
      })
      .catch(error => {
        console.error('❌ [PLAN-EDIT] Error fetching levels:', error);
      });

    gradesService
      .getGrades()
      .then(grds => {
        console.log('✅ [PLAN-EDIT] Grades fetched:', grds);
        setGrades(grds);
      })
      .catch(error => {
        console.error('❌ [PLAN-EDIT] Error fetching grades:', error);
      });

    curriculumsService
      .getCurriculums()
      .then(currs => {
        console.log('✅ [PLAN-EDIT] Curriculums fetched:', currs);
        setCurriculums(currs);
      })
      .catch(error => {
        console.error('❌ [PLAN-EDIT] Error fetching curriculums:', error);
      });
  }, []);

  // Fetch all daily exercises for search
  useEffect(() => {
    dailyExerciseService
      .getAllDailyExercises(1, 1000) // Fetch first 1000 exercises
      .then(resp => {
        const exercises = resp?.data || [];
        setAllExercises(exercises);

        // Create exercise map for quick lookup
        const map: {[id: string]: any} = {};
        exercises.forEach((ex: any) => {
          const exId = ex.id || ex._id;
          if (exId) {
            map[exId] = ex;
          }
        });
        setExerciseMap(map);
      })
      .catch(error => {
        console.error('❌ [PLAN-EDIT] Error fetching exercises:', error);
      });
  }, []);

  // Debounce search query by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Execute search when debounced value changes
  useEffect(() => {
    const handleSearch = async (term: string) => {
      setIsSearching(true);
      try {
        const response = await dailyExerciseService.searchDailyExercisesByName(
          term,
          1,
          5000, // Large limit to find all matching exercises
        );
        const exercises = response?.data || [];
        setSearchResults(exercises);
      } catch (error) {
        console.error('❌ [PLAN-EDIT] Error searching exercises:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    if (debouncedSearchQuery.length >= 3) {
      handleSearch(debouncedSearchQuery);
    } else if (debouncedSearchQuery.length === 0) {
      setSearchResults([]);
    }
  }, [debouncedSearchQuery]);

  // Fetch plan data
  useEffect(() => {
    plansService
      .getNewPlanById(params.planId)
      .then(resp => {
        setIsLoading(false);
        setPlan(resp);

        // Populate editable fields
        setPlanName(resp?.name || '');
        setPlanDuration(resp?.duration || 0);

        // Set selected level ID
        const levelId =
          typeof resp.level === 'object'
            ? resp.level?.id || resp.level?._id
            : resp.level;
        setSelectedLevelId(levelId || undefined);

        // Set selected grade ID
        const gradeId =
          typeof resp.grade === 'object'
            ? resp.grade?.id || resp.grade?._id
            : resp.grade;
        setSelectedGradeId(gradeId || undefined);

        // Set selected curriculum ID
        const curriculumId =
          typeof resp.curriculum === 'object'
            ? resp.curriculum?.id || resp.curriculum?._id
            : resp.curriculum;
        setSelectedCurriculumId(curriculumId || undefined);

        console.log('✅ [PLAN-EDIT] Plan loaded with IDs:', {
          levelId,
          gradeId,
          curriculumId,
        });
      })
      .catch(error => {
        console.error('❌ [PLAN-EDIT] Error fetching plan:', error);
        notification.error({
          message: 'Fetch Error',
          description: 'Failed to fetch plan data. Please try again.',
        });
        setIsLoading(false);
        setIsError(true);
      });
  }, [params.planId]);

  // Handle level selection with confirmation popup
  const handleLevelChange = (levelId: string) => {
    if (selectedGradeId) {
      Modal.confirm({
        title: 'Replace Grade with Level?',
        content:
          'The currently selected grade will be removed. You are switching to a level-based plan. Do you want to continue?',
        okText: 'Yes, replace',
        cancelText: 'Cancel',
        onOk: () => {
          setSelectedGradeId(undefined);
          setSelectedLevelId(levelId);
        },
      });
    } else {
      setSelectedLevelId(levelId);
    }
  };

  // Handle grade selection with confirmation popup
  const handleGradeChange = (gradeId: string) => {
    if (selectedLevelId) {
      Modal.confirm({
        title: 'Replace Level with Grade?',
        content:
          'The currently selected level will be removed. You are switching to a grade-based plan. Do you want to continue?',
        okText: 'Yes, replace',
        cancelText: 'Cancel',
        onOk: () => {
          setSelectedLevelId(undefined);
          setSelectedGradeId(gradeId);
        },
      });
    } else {
      setSelectedGradeId(gradeId);
    }
  };

  // Load exercises for a specific day
  const loadDayExercises = async (dayNumber: number) => {
    // Check if already loaded or loading
    if (loadedDayDetails.has(dayNumber) || loadingDays.has(dayNumber)) {
      return;
    }

    setLoadingDays(new Set(loadingDays).add(dayNumber));

    try {
      const exercises = await plansService.getDailyExercisesByPlanAndDay(
        params.planId,
        dayNumber,
      );

      // Create "name+id" strings for each exercise
      const exerciseStrings = exercises.map((ex: any) => {
        const exId = ex.id || ex._id;
        const exName = ex.name || exId;
        return exName + '+' + exId;
      });

      // Store in originalDayExercises if first load
      setOriginalDayExercises(prev => {
        if (prev.has(dayNumber)) return prev;
        return new Map(prev).set(dayNumber, exerciseStrings);
      });

      // Store in currentDayExercises if first load
      setCurrentDayExercises(prev => {
        if (prev.has(dayNumber)) return prev;
        return new Map(prev).set(dayNumber, exerciseStrings);
      });

      // Store full exercise details
      setLoadedDayDetails(prev => new Map(prev).set(dayNumber, exercises));

      // Remove from loading set
      setLoadingDays(prev => {
        const next = new Set(prev);
        next.delete(dayNumber);
        return next;
      });
    } catch (error) {
      console.error(
        '❌ [PLAN-EDIT] Error loading exercises for day',
        dayNumber,
        ':',
        error,
      );

      // Remove from loading set
      setLoadingDays(prev => {
        const next = new Set(prev);
        next.delete(dayNumber);
        return next;
      });
    }
  };

  const renderDay = (plan: any) => {
    const duration = plan?.duration || 365;

    // Use search results if searching, otherwise use all fetched exercises
    const filteredExercises =
      searchQuery.length >= 3 ? searchResults : allExercises;

    return Array.from({length: duration}, (_, dayIndex) => {
      const dayNumber = dayIndex + 1;
      const currentExercises = currentDayExercises.get(dayIndex) || [];
      const isLoadingDay = loadingDays.has(dayIndex);

      return (
        <Panel
          header={`Day ${dayNumber}`}
          key={dayIndex}
          extra={
            isLoadingDay
              ? 'Loading...'
              : `${currentExercises.length} exercise(s)`
          }>
          {isLoadingDay ? (
            <Spin tip="Loading exercises..." />
          ) : (
            <Select
              autoClearSearchValue={false}
              mode="multiple"
              loading={isSearching}
              style={{width: '100%'}}
              placeholder="Please select exercises for this day"
              filterOption={false}
              value={currentExercises}
              onSearch={searchValue => {
                setSearchQuery(searchValue);
              }}
              onChange={idWithNameList => {
                // Keep the full "name+id" strings
                const exerciseStrings = idWithNameList || [];

                // Update currentDayExercises map
                setCurrentDayExercises(prev => {
                  const map = new Map(prev);
                  map.set(dayIndex, exerciseStrings);
                  return map;
                });
              }}>
              {filteredExercises.map((ex: any) => {
                const exId = ex.id || ex._id;
                return (
                  <Select.Option key={exId} value={ex.name + '+' + exId}>
                    <p>{ex.name}</p>
                  </Select.Option>
                );
              })}
            </Select>
          )}
        </Panel>
      );
    });
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Plan Edit</Title>

      {/* Plan metadata editing UI */}
      {!isError && !isLoading && plan && (
        <div
          style={{
            marginBottom: 20,
            padding: 20,
            border: '1px solid #d9d9d9',
            borderRadius: 4,
          }}>
          <Row>
            <Col span={12}>
              <div style={{marginBottom: 10}}>
                <Text style={{display: 'block'}} strong={true}>
                  Plan Name *
                </Text>
                <Input
                  value={planName}
                  onChange={e => setPlanName(e.target.value)}
                  placeholder="Enter plan name"
                  style={{width: '100%'}}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{marginBottom: 10, paddingLeft: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Duration (days) *
                </Text>
                <InputNumber
                  value={planDuration}
                  onChange={value => setPlanDuration(value || 0)}
                  placeholder="Enter duration"
                  style={{width: '100%'}}
                  min={1}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{marginBottom: 10}}>
                <Text style={{display: 'block'}} strong={true}>
                  Curriculum *
                </Text>
                <Select
                  value={selectedCurriculumId}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select a Curriculum"
                  optionFilterProp="children"
                  onChange={curriculumId =>
                    setSelectedCurriculumId(curriculumId)
                  }>
                  {curriculums?.map(curriculum => (
                    <Select.Option
                      key={curriculum.id || curriculum._id}
                      value={curriculum.id || curriculum._id}>
                      {curriculum.name}
                      {curriculum.isAcademic ? ' (Academic)' : ''}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{marginBottom: 10}}>
                <Text style={{display: 'block'}} strong={true}>
                  Level{' '}
                  {selectedGradeId ? (
                    <span style={{color: '#999', fontWeight: 'normal'}}>
                      (disabled — grade is selected)
                    </span>
                  ) : (
                    '*'
                  )}
                </Text>
                <Select
                  value={selectedLevelId}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select a Level"
                  optionFilterProp="children"
                  disabled={!!selectedGradeId}
                  allowClear
                  onClear={() => setSelectedLevelId(undefined)}
                  onChange={handleLevelChange}>
                  {levels?.map(level => (
                    <Select.Option
                      key={level.id || level._id}
                      value={level.id || level._id}>
                      {level.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
            <Col span={12}>
              <div style={{marginBottom: 10, paddingLeft: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Grade{' '}
                  {selectedLevelId ? (
                    <span style={{color: '#999', fontWeight: 'normal'}}>
                      (disabled — level is selected)
                    </span>
                  ) : (
                    '*'
                  )}
                </Text>
                <Select
                  value={selectedGradeId}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select a Grade"
                  optionFilterProp="children"
                  disabled={!!selectedLevelId}
                  allowClear
                  onClear={() => setSelectedGradeId(undefined)}
                  onChange={handleGradeChange}>
                  {grades?.map(grade => (
                    <Select.Option
                      key={grade.id || grade._id}
                      value={grade.id || grade._id}>
                      {grade.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>
        </div>
      )}

      {!isError && !isLoading && (
        <>
          <Collapse
            onChange={activeKeys => {
              // Load exercises for expanded panels
              if (Array.isArray(activeKeys)) {
                activeKeys.forEach(key => {
                  const dayIndex = Number(key);
                  if (!isNaN(dayIndex)) {
                    loadDayExercises(dayIndex);
                  }
                });
              }
            }}>
            {plan && renderDay(plan)}
          </Collapse>
        </>
      )}
      <div style={{width: '100%', textAlign: 'center', marginTop: '20px'}}>
        <Button
          style={{}}
          size={'large'}
          loading={isSaving}
          disabled={isSaving}
          onClick={async () => {
            // Validate required fields
            if (!planName || planName.trim() === '') {
              notification.warning({
                message: 'Missing Plan Name',
                description: 'Please enter a plan name.',
              });
              return;
            }

            if (!planDuration || planDuration <= 0) {
              notification.warning({
                message: 'Missing Duration',
                description: 'Please enter a valid duration.',
              });
              return;
            }

            if (!selectedCurriculumId) {
              notification.warning({
                message: 'Missing Curriculum',
                description: 'Please select a curriculum.',
              });
              return;
            }

            if (!selectedLevelId && !selectedGradeId) {
              notification.warning({
                message: 'Missing Level or Grade',
                description: 'Please select either a level or a grade.',
              });
              return;
            }

            setIsSaving(true);

            const planId = plan?.id;

            try {
              // Step 1: Update the plan metadata
              console.log('🔍 [PLAN-EDIT] Step 1: Updating plan metadata...');

              await plansService.newUpdatePlan(
                planId,
                planName.trim(),
                planDuration,
                selectedLevelId || undefined,
                selectedGradeId || undefined,
                selectedCurriculumId,
                plan.placementTest,
              );

              // Get all day indices that have been loaded (and potentially modified)
              const allDayIndices = new Set([
                ...Array.from(originalDayExercises.keys()),
                ...Array.from(currentDayExercises.keys()),
              ]);

              for (const dayIndex of allDayIndices) {
                const originalExercises =
                  originalDayExercises.get(dayIndex) || [];
                const currentExercises =
                  currentDayExercises.get(dayIndex) || [];

                // Extract IDs from "name+id" strings
                const originalIds = originalExercises.map(
                  (str: string) => str.split('+')[1],
                );
                const currentIds = currentExercises.map(
                  (str: string) => str.split('+')[1],
                );

                // Find exercises to add (in current but not in original)
                const exercisesToAdd = currentIds.filter(
                  (exId: string) => !originalIds.includes(exId),
                );

                // Find exercises to remove (in original but not in current)
                const exercisesToRemove = originalIds.filter(
                  (exId: string) => !currentIds.includes(exId),
                );

                // Add new exercises
                for (const exerciseId of exercisesToAdd) {
                  await plansService.addExerciseToDay(
                    planId,
                    dayIndex,
                    exerciseId,
                  );
                }

                // Remove old exercises
                for (const exerciseId of exercisesToRemove) {
                  await plansService.removeExerciseFromDay(
                    planId,
                    dayIndex,
                    exerciseId,
                  );
                }
              }

              notification.success({
                message: 'Plan Updated!',
                description: 'The plan changes have been saved successfully.',
                duration: 4,
              });
              setIsSaving(false);
              history.push('/plans/' + planId);
            } catch (error: any) {
              console.error('❌ [PLAN-EDIT] Error updating plan:', error);
              notification.error({
                message: 'Update Failed',
                description:
                  error?.message || 'Plan update failed. Please try again.',
                duration: 6,
              });
              setIsSaving(false);
            }
          }}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Plan fetching error ⚠️</Title>}
    </div>
  );
};
